import { useQuery } from '@apollo/client/react';
import {
  BodyText,
  Heading,
  InlineLink,
  Link,
} from '@utilitywarehouse/hearth-react';
import { PhoneMediumIcon } from '@utilitywarehouse/hearth-react-icons';
import { useLocation } from 'react-router-dom';

import { Dialog, DialogContent, DialogID, useDialog } from '@components/Dialog';
import { paths } from '@constants/paths';
import { gql } from '@generated/gql';

import './styles.scss';

const callHoursQuery = gql(`
  query CallHours {
    callHours {
      isOnCallHours
    }
  }
`);

// Presentational component - pure UI, no hooks
interface InboundCallUIProps {
  onOpen: () => void;
  onClose: () => void;
  dialogId: DialogID;
}

const InboundCallUI = ({ onOpen, onClose, dialogId }: InboundCallUIProps) => (
  <>
    <Link inverted asChild>
      <button type="button" onClick={onOpen}>
        <PhoneMediumIcon /> Can we help?
      </button>
    </Link>
    <Dialog id={dialogId} onClose={onClose}>
      <DialogContent>
        <Heading as="h2" size="lg" className="inboundCall__Heading">
          Give us a call
        </Heading>
        <BodyText>
          Give our friendly team a call on{' '}
          <InlineLink href="tel:03337770228">0333 777 0228</InlineLink> and
          we'll be happy to help. Our average call wait time is under 3 minutes.
        </BodyText>
      </DialogContent>
    </Dialog>
  </>
);

// Container component - handles all data fetching and logic
const InboundCall = () => {
  const { pathname } = useLocation();
  const ignoredPaths = [paths.address, paths.thankYou];
  const dialog = useDialog(DialogID.INBOUND_CALL);
  const { data, loading } = useQuery(callHoursQuery, {
    fetchPolicy: 'network-only',
    nextFetchPolicy: 'cache-first',
  });

  const isOnCallHours = data?.callHours?.isOnCallHours ?? false;
  const isIgnoredPath = ignoredPaths.includes(pathname);

  if (!isOnCallHours || isIgnoredPath || loading) {
    return null;
  }

  return (
    <InboundCallUI
      dialogId={dialog.id}
      onClose={() => dialog.setOpen(false)}
      onOpen={() => dialog.setOpen(true)}
    />
  );
};

export { InboundCall, InboundCallUI, callHoursQuery };
export type { InboundCallUIProps };
